import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, filter, map, Observable, of } from 'rxjs';
import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { SurveyService } from '@ps/survey-data-access';
import {
  isLaunchPage,
  LaunchSameSettings,
  Survey,
  SurveyKind,
  SurveyLaunchFormState,
  ProjectMemberSync,
  TraditionalLocaleSync,
} from '@ps/survey-utils';

import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import {
  selectCoreChildren,
  selectParticipatingSurveys,
  selectSurveyId,
  selectSurveyWorkflowPage,
} from '../core/survey-creation-core.selectors';
import { participatingSiblingIds } from '../core/utils/core-children.helper';
import { SurveyChildRegistryState } from '../survey-creation-state.model';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyCreationLaunchApiActions } from './survey-creation-launch.actions';
import {
  selectLaunchChildren,
  selectLaunchForm,
  selectLaunchSameSettings,
} from './survey-creation-launch.selectors';

/**
 * Multi-country launch settings once the active locale is saved on the launch
 * page. Traditional MC: its own values are PATCHed to it and its checked
 * "same …" settings to every sibling. Group MC and Tracker: the project PUT
 * already wrote every child, so only the registry is brought up to date. Split from
 * `SurveyCreationLaunchEffects` — one trigger → outcome flow per file.
 */
@Injectable()
export class SurveyCreationLaunchLocaleSyncEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _surveyService = inject(SurveyService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);

  // Legacy: footer save / locale switch → PUT current, then PATCH every locale.
  // concatMap so a second save never cancels the first one's PATCHes.
  public readonly syncTraditionalLocales$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.saveSurveySuccess),
      filter(({ survey }) => SurveyKind.isTraditionalMc(survey)),
      concatLatestFrom(() => [
        this._store.select(selectSurveyWorkflowPage),
        this._store.select(selectLaunchForm),
        this._store.select(selectLaunchSameSettings),
        this._store.select(selectCoreChildren),
      ]),
      filter(([, page]) => isLaunchPage(page)),
      concatMap(([{ survey }, , launchForm, sameSettings, children]) =>
        this._syncLocales(survey, launchForm, sameSettings, children),
      ),
    ),
  );

  // The project response is the project row, not a locale: the registry takes
  // the values every locale shares from it, and each locale's own values from
  // what the save sent it.
  public readonly syncProjectChildren$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.saveSurveySuccess),
      filter(({ survey }) => SurveyKind.isGroupMcOrTracker(survey)),
      concatLatestFrom(() => [
        this._store.select(selectLaunchForm),
        this._store.select(selectLaunchSameSettings),
        this._store.select(selectLaunchChildren),
        this._store.select(selectSurveyId),
        this._store.select(selectParticipatingSurveys),
      ]),
      map(
        ([
          { survey },
          launchForm,
          sameSettings,
          childLaunches,
          activeSurveyId,
          members,
        ]) =>
          SurveyCreationLaunchApiActions.projectLaunchSettingsSynced({
            updates: ProjectMemberSync.from({
              launch: launchForm.value,
              sameSettings,
              childLaunches,
            }).updatesFor(activeSurveyId, members, survey),
          }),
      ),
    ),
  );

  private _syncLocales(
    survey: Survey,
    launchForm: SurveyLaunchFormState,
    sameSettings: LaunchSameSettings,
    children: Record<SurveyChildKey, SurveyChildRegistryState>,
  ): Observable<Action> {
    const updates = TraditionalLocaleSync.from({
      launch: launchForm.value,
      sameSettings,
      isOperator: this._authSelectors.isOperator(),
      savedUniqueLinks: Option.from(survey.uniqueLinks),
    }).updatesFor(survey.id, participatingSiblingIds(children, survey.id));

    return this._surveyService.patchTraditionalLocales(updates).pipe(
      map(() =>
        SurveyCreationLaunchApiActions.localeSettingsSynced({ updates }),
      ),
      // Legacy: the raw API message is toasted; the active survey stays saved.
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationLaunchApiActions.localeSettingsSyncFailure({
              error: error.message,
            }),
          ),
        { notify: true },
      ),
    );
  }
}
